<template>
  <div class="easypaisa-page options-card-wrapper--no-padding options-card-wrapper--full-height">
    <div class="easypaisa-page__card">
      <div class="easypaisa-page__header">
        <div class="easypaisa-page__header-left"></div>

        <div class="easypaisa-page__logo">
          <img v-if="logo" :src="logo" />
        </div>

        <div class="easypaisa-page__header-right">
          <div class="easypaisa-page__locale" ref="localeMenu" @click="toggleLocaleMenu">
            <span class="easypaisa-page__locale-text">{{ currentLocale.toUpperCase() }}</span>
            <svg
              class="easypaisa-page__locale-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 6L8 10L12 6"
                stroke="#2a2637"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <div v-if="localeMenuOpen" class="easypaisa-page__locale-dropdown">
              <div
                v-for="locale in locales"
                :key="locale"
                class="easypaisa-page__locale-item"
                :class="{ active: locale === currentLocale }"
                @click.stop="selectLocale(locale)">
                {{ locale.toUpperCase() }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="easypaisa-page__amount">
        <span v-if="symbol" class="easypaisa-page__amount-symbol">{{ symbol }}</span>
        <span class="easypaisa-page__amount-value">{{ formattedAmount }}</span>
        <span v-if="!symbol" class="easypaisa-page__amount-currency">{{ currency }}</span>
      </div>

      <!-- Status center section -->
      <div class="easypaisa-page__center easypaisa-status__center">
        <template v-if="stateType === 'processing'">
          <div class="easypaisa-status__spinner"></div>
          <div class="easypaisa-status__label">{{ $t('easypaisa_status__processing_label') }}</div>
        </template>

        <!-- Failed -->
        <template v-else-if="stateType === 'failed'">
          <div class="easypaisa-status__label">{{ $t('easypaisa_status__failed_title') }}</div>
        </template>

        <!-- Success -->
        <template v-else>
          <div class="easypaisa-status__label">{{ $t('easypaisa_status__processing') }}</div>

          <div class="easypaisa-status__details-card">
            <div class="easypaisa-status__details-title">
              {{ $t('easypaisa_status__payment_details') }}
            </div>
            <div class="easypaisa-status__detail-row">
              <span class="easypaisa-status__detail-label">{{
                $t('easypaisa_status__initiation_date')
              }}</span>
              <span class="easypaisa-status__detail-value">{{ formattedDate }}</span>
            </div>
            <div class="easypaisa-status__detail-row easypaisa-status__detail-row--last">
              <span class="easypaisa-status__detail-label">{{
                $t('easypaisa_status__amount')
              }}</span>
              <span class="easypaisa-status__detail-value">{{ displayAmount }}</span>
            </div>
          </div>
        </template>
      </div>

      <div class="easypaisa-page__actions">
        <template v-if="stateType === 'failed'">
          <button
            class="easypaisa-page__btn easypaisa-page__btn--secondary"
            @click="$emit('redirect-to-merchant')">
            {{ $t('easypaisa__close') }}
          </button>
          <div class="easypaisa-page__actions-divider"></div>
          <button
            class="easypaisa-page__btn easypaisa-page__btn--primary"
            @click="$emit('redirect-to-merchant')">
            {{ $t('easypaisa__try_again') }}
          </button>
        </template>
        <template v-else>
          <button
            class="easypaisa-page__btn easypaisa-page__btn--primary easypaisa-status__return-btn"
            @click="$emit('redirect-to-merchant')">
            {{ $t('easypaisa_status__return') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import EasypaisaIcon from '../assets/easypaisa.svg';
import JazzCashIcon from '../assets/jazzcash.svg';
import { messages } from '../translations/index';
import parseAvailableLocales from '@/helpers/parseAvailableLocales';

const SUCCESS_STATUSES = new Set([
  'processed',
  'authorized',
  'partially_processed',
  'refunded',
  'revoked',
  'charged_back',
  'voided',
]);

const FAILED_STATUSES = new Set(['process_failed', 'invoke_failed', 'terminated', 'expired']);

export default {
  name: 'EasypaisaStatusPage',

  created() {
    document.addEventListener('click', this.closeLocaleMenu);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeLocaleMenu);
  },

  data() {
    return {
      localeMenuOpen: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData', 'checkoutOptions']),
    ...mapState('UI', ['availableLocales', 'queryLocale']),

    paymentInvoice() {
      return this.paymentInvoiceData?.data || this.paymentInvoiceData || {};
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    locales() {
      const optionLocales = parseAvailableLocales(
        this.availableLocales,
        this.checkoutOptions?.available_locales,
        Object.keys(messages)
      );
      return optionLocales.length ? optionLocales : Object.keys(messages);
    },

    stateType() {
      const status = this.paymentInvoice.status || '';
      if (SUCCESS_STATUSES.has(status)) return 'success';
      if (FAILED_STATUSES.has(status)) return 'failed';
      return 'processing';
    },

    statusText() {
      if (this.stateType === 'failed') return this.$t('easypaisa_status__failed_title');
      return this.$t('easypaisa_status__processing');
    },

    logo() {
      const methodCode = this.flowData?.payload?.method?.code;
      if (methodCode === 'easypaisa') return EasypaisaIcon;
      if (methodCode === 'jazzcash') return JazzCashIcon;
      return this.flowData?.payload?.method?.logo || null;
    },

    currency() {
      return this.flowData?.payload?.currency || this.flowData?.currency || '';
    },

    symbol() {
      switch (this.currency) {
        case 'PKR':
          return '₨';
        case 'UAH':
          return '₴';
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
        default:
          return null;
      }
    },

    formattedAmount() {
      const amount =
        this.paymentInvoice.amount ?? this.flowData?.payload?.amount ?? this.flowData?.amount;
      return amount != null
        ? Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0 })
        : '';
    },

    displayAmount() {
      return this.symbol
        ? `${this.symbol}${this.formattedAmount}`
        : `${this.formattedAmount} ${this.currency}`;
    },

    formattedDate() {
      const ts = this.paymentInvoice.created;
      if (!ts) return '—';
      return this.$dayjs(ts * 1000).format('DD.MM.YYYY HH:mm');
    },
  },

  methods: {
    selectLocale(locale) {
      this.localeMenuOpen = false;
      this.$bus.$emit('changeLocale', locale);
    },

    toggleLocaleMenu() {
      this.localeMenuOpen = !this.localeMenuOpen;
    },

    closeLocaleMenu(event) {
      if (!this.$refs.localeMenu || !this.$refs.localeMenu.contains(event.target)) {
        this.localeMenuOpen = false;
      }
    },
  },
};
</script>

<style lang="scss">
.easypaisa-status {
  &__center {
    padding: 28px 12px;
    justify-content: center;
  }

  @keyframes easypaisa-status-spin {
    to {
      transform: rotate(360deg);
    }
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(250, 250, 255, 0.3);
    border-top-color: #fafaff;
    border-radius: 50%;
    animation: easypaisa-status-spin 0.8s linear infinite;
    align-self: center;
  }

  &__label {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #fafaff;
    text-align: center;
    letter-spacing: -0.01em;
    margin: 20px;
  }

  &__details-card {
    width: 100%;
    border: 1px solid rgba(250, 250, 255, 0.28);
    border-radius: 4px;
    padding: 16px;
  }

  &__details-title {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: #fafaff;
    margin-bottom: 10px;
    text-align: start;
  }

  &__detail-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;

    &--last {
      padding-bottom: 0;
      margin-bottom: 0;
      border-bottom: none;
    }
  }

  &__detail-label {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #fafaff;
  }

  &__detail-value {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: #fafaff;
    text-align: right;
  }

  &__return-btn {
    flex: 1;
  }
}
</style>
